import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';

import { IChannel } from '../types/chat';
import { channelKey } from '../utils/channelKey';
import { websocketService } from '../services/websocketService';
import { apiService } from '../services/apiService';
import { networkEventManager } from '../services/networkEventManager';
import { WS_EVENTS, APP_EVENTS } from '../types/wsEvents';
import { SIDEBAR_TAB_TYPES } from '../types/sidebar';

interface ChannelsContextType {
  channels: IChannel[];
  loading: boolean;
  loadingMore: boolean;
  hasNextPage: boolean;
  totalCount?: number;
  filterByClaimId: string | null;
  sidebarSelectedTab: SIDEBAR_TAB_TYPES;
  setFilterByClaimId: (claimId: string | null) => void;
  setChannels: (channels: IChannel[]) => void;
  refreshChannels: () => Promise<void>;
  loadMoreChannels: () => Promise<void>;
  updateChannelMessages: (chatId: string, messages: any[]) => void;
  addMessageToChannel: (chatId: string, message: any) => void;
  updateChannelModel: (chatId: string, model: string) => void;
  deleteChannel: (chatName: string) => void;
  renameChannel: (chatName: string, newName: string) => void;
  toggleChannelPin: (chatName: string, isPinned: boolean) => void;
  closeChannel: (chatName: string, closedAt?: string) => void;
  toggleChannelShared: (chatId: string, isShared: boolean) => void;
  updateChannelTitle: (chatId: string, newTitle: string) => void;
  reorderChannels: (orderedChannels: IChannel[]) => void | Promise<void>;
  updateSidebarSelectedTab: (sidebarSelectedTab: SIDEBAR_TAB_TYPES) => void
}

const ChannelsContext = createContext<ChannelsContextType | undefined>(undefined);

export const useChannels = () => {
  const context = useContext(ChannelsContext);
  if (context === undefined) {
    throw new Error('useChannels must be used within a ChannelsProvider');
  }
  return context;
};

interface ChannelsProviderProps {
  children: ReactNode;
}

export const ChannelsProvider: React.FC<ChannelsProviderProps> = ({ children }) => {
  const [channels, setChannels] = useState<IChannel[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [totalCount, setTotalCount] = useState<number | undefined>();
  const [currentPage, setCurrentPage] = useState(1);
  const [sidebarSelectedTab, setSidebarSelectedTab] = useState(SIDEBAR_TAB_TYPES.all)

  /**
   * Three-state filter for claim-based chat filtering:
   * - null (default): No filter active, shows user's own chats
   * - string (claim ID): Non-empty claim filter, shows chats for that specific claim across all tenants
   * - empty string '': Claims mode with no claim selected, shows no chats (prevents accidentally showing wrong claim's chats)
   */
  const [filterByClaimId, setFilterByClaimId] = useState<string | null>(null);
  const shouldLoadMoreAfterDeleteRef = useRef(false);

  // Use ref instead of module-level variable for better React integration
  // This prevents issues with hot reloading in development
  const initDoneRef = useRef(false);

  // Track the last filter value we successfully fetched to avoid redundant API calls
  const lastFetchedFilterRef = useRef<string | null | undefined>(undefined);

  const updateSidebarSelectedTab = (sidebarTab: SIDEBAR_TAB_TYPES) => {
    if(sidebarTab === sidebarSelectedTab) { return }
    setSidebarSelectedTab(sidebarTab)
  }

  const handleSetFilterByClaimId = (claimId: string | null) => {
    // Use the updater form rather than comparing against the closed-over
    // `filterByClaimId`: if a caller ever fires two calls in one synchronous
    // handler, React hasn't re-rendered between them, so a closure comparison
    // would read the pre-click value for both and could wrongly no-op the
    // second call. The updater form always sees the latest queued value, so
    // it only no-ops when nothing would actually change.
    setFilterByClaimId(prev => (prev !== claimId ? claimId : prev));
  };

  const refreshChannels = useCallback(async () => {
    // Capture the filter value for this call so we can detect if a newer call
    // has superseded us while we were awaiting the API response.
    try {
      setLoading(true);
      setCurrentPage(1);

       // null — no filter active, fetch all user's chats
        console.log('[ChannelsContext] Fetching all channels...');
        const result = await websocketService.listChannels(1);

        console.log('[ChannelsContext] Received channels result:', result);
        setChannels(result.channels);
        setHasNextPage(false);
        setTotalCount(result.totalCount);
    } catch (error) {
      console.error('[ChannelsContext] Error fetching channels:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMoreChannels = useCallback(async () => {
    // Since listChannels now fetches all pages, this is effectively a no-op
    // But we keep it for backward compatibility and in case we need pagination later
    if (loadingMore || !hasNextPage) return;

    try {
      setLoadingMore(true);
      const nextPage = currentPage + 1;
      const result = await websocketService.listChannels(nextPage);
      const updateChannels = (prev: IChannel[]) => {
        // Prevent duplicates by checking if channel already exists
        const existingIds = new Set(prev.map(c => c.id));
        const newChannels = result.channels.filter((c: IChannel) => !existingIds.has(c.id));
        return [...prev, ...newChannels];
      };
      setChannels(updateChannels);
      setHasNextPage(result.hasNextPage);
      setCurrentPage(nextPage);
    } catch (error) {
      console.error('[ChannelsContext] Error loading more channels:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasNextPage, currentPage]);

  const updateChannelMessages = (chatId: string, messages: any[]) => {
    setChannels(prev => 
      prev.map(channel => 
        channel.id === chatId 
          ? { 
              ...channel, 
              metadata: { 
                ...channel.metadata, 
                messages: messages 
              } 
            }
          : channel
      )
    );
  };

  const addMessageToChannel = (chatId: string, message: any) => {
    setChannels(prev => 
      prev.map(channel => {
        if (channel.id !== chatId) {
          return channel;
        }

        const currentMessages = channel.metadata?.messages || [];
        
        // Check if message with this ID already exists (for all message types)
        if (message.id) {
          const existingIndex = currentMessages.findIndex((msg: any) => msg.id === message.id);
          
          if (existingIndex !== -1) {
            // Update existing message (handles partial/complete/final updates)
            const updatedMessages = [...currentMessages];
            updatedMessages[existingIndex] = {
              ...updatedMessages[existingIndex],
              ...message,
              // Preserve existing content if new content is empty/undefined
              content: message.content || updatedMessages[existingIndex].content,
            };
            return {
              ...channel,
              metadata: {
                ...channel.metadata,
                messages: updatedMessages
              }
            };
          }
        }
        
        // For new messages (no existing ID match), add to the list
        // Also check for duplicate content to prevent accidental duplicates
        // Check by ID first (handles undefined IDs)
        const hasDuplicateId = message.id && currentMessages.some((msg: any) => msg.id === message.id);
        if (hasDuplicateId) {
          // Message with this ID already exists, return unchanged
          return channel;
        }
        
        // Check for duplicate content (for messages without IDs or with undefined IDs)
        const isDuplicate = currentMessages.some((msg: any) => {
          // If both have IDs and they match, it's a duplicate
          if (msg.id && message.id && msg.id === message.id) {
            return true;
          }
          // If content, role, and timestamp are very close, it's likely a duplicate
          if (msg.content === message.content && 
              msg.role === message.role && 
              msg.created_at && message.created_at &&
              Math.abs(new Date(msg.created_at).getTime() - new Date(message.created_at).getTime()) < 1000) {
            return true;
          }
          return false;
        });
        
        if (isDuplicate) {
          // Message already exists, return unchanged
          console.log(`[addMessageToChannel] Skipping duplicate message for chat ${chatId}, id: ${message.id}, content: ${message.content?.substring(0, 50)}`);
          return channel;
        }
        
        return {
          ...channel,
          metadata: {
            ...channel.metadata,
            messages: [...currentMessages, message]
          }
        };
      })
    );
  };

  const updateChannelModel = (chatId: string, model: string) => {
    setChannels(prev =>
      prev.map(channel =>
        channel.id === chatId
          ? {
              ...channel,
              metadata: {
                ...channel.metadata,
                model,
              },
            }
          : channel
      )
    );
  };

  const deleteChannel = (chatName: string) => {
    setChannels(prev => {
      const updatedChannels = prev.filter(channel => channel.name !== chatName);
      // If there are 19 chats remaining after deletion, mark to load more
      if (updatedChannels.length === 19) {
        shouldLoadMoreAfterDeleteRef.current = true;
      }
      return updatedChannels;
    });
  };

  const renameChannel = (chatName: string, newName: string) => {
    setChannels(prev => 
      prev.map(channel => 
        channel.name === chatName 
          ? { ...channel, label: newName }
          : channel
      )
    );
  };

  const toggleChannelPin = (chatName: string, isPinned: boolean) => {
    setChannels(prev => 
      prev.map(channel => 
        channel.name === chatName 
          ? { ...channel, pinned: isPinned }
          : channel
      )
    );
  };

  const closeChannel = (chatName: string, closedAt?: string) => {
    setChannels(prev =>
      prev.map(channel =>
        channel.name === chatName || channel.id === chatName
          ? {
              ...channel,
              status: 'closed',
              ...(closedAt ? { closed_at: closedAt } : {}),
            }
          : channel
      )
    );
  };

  const toggleChannelShared = (chatId: string, isShared: boolean) => {
    setChannels(prev =>
      prev.map((channel) => {
        const key = channelKey(channel);
        return key === chatId ? { ...channel, shared: isShared } : channel;
      }),
    );
  };

  const reorderChannels = useCallback(async (orderedChannels: IChannel[]) => {
    const previousChannels = channels;
    setChannels(orderedChannels);

    const chatIds = orderedChannels.map((c) => channelKey(c));
    const response = await apiService.reorderChats(chatIds);
    if (!response.success) {
      setChannels(previousChannels);
      lastFetchedFilterRef.current = undefined;
      await refreshChannels();
    }
  }, [channels, refreshChannels]); // channels needed to capture previous on failure

  const updateChannelTitle = (chatId: string, newTitle: string) => {
    setChannels(prev => 
      prev.map(channel => 
        channel.id === chatId 
          ? { ...channel, label: newTitle }
          : channel
      )
    );
  };

  // Watch for when channels.length becomes 19 after deletion and load more if needed
  useEffect(() => {
    if (shouldLoadMoreAfterDeleteRef.current && channels.length === 19 && hasNextPage && !loadingMore && !loading) {
      shouldLoadMoreAfterDeleteRef.current = false;
      loadMoreChannels();
    }
  }, [channels.length, hasNextPage, loadingMore, loading, loadMoreChannels]);

  useEffect(() => {
    // Use ref-based guard to prevent duplicate initialization
    // Handles React StrictMode double-invoke in development
    if (initDoneRef.current) {
      return;
    }
    initDoneRef.current = true;
    refreshChannels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for locally-added channels to prepend without API call
  // Set up listener separately so it always works, even if guard prevents initial effect
  useEffect(() => {
    const handleChannelAdd = (ch: IChannel) => {
      setChannels(prev => {
        // avoid duplicates - check by id only (most reliable)
        if (prev.some(c => c.id === ch.id)) {
          return prev;
        }
        return [ch, ...prev];
      });
    };
    const handleSetSidebarTab = (e: Event) => {
      const tab = (e as CustomEvent<SIDEBAR_TAB_TYPES>).detail;
      if (tab) setSidebarSelectedTab(tab);
    };

    websocketService.on(WS_EVENTS.CHANNEL_ADD, handleChannelAdd);
    window.addEventListener(APP_EVENTS.SET_SIDEBAR_TAB, handleSetSidebarTab);

    return () => {
      websocketService.off(WS_EVENTS.CHANNEL_ADD, handleChannelAdd);
      window.removeEventListener(APP_EVENTS.SET_SIDEBAR_TAB, handleSetSidebarTab);
    };
  }, []);

  // Invalidate filter cache on auth failure to prevent stale data across user sessions
  useEffect(() => {
    const handleAuthFailure = () => {
      lastFetchedFilterRef.current = undefined;
      console.debug('[ChannelsContext] Auth failure detected, invalidated filter cache');
    };

    networkEventManager.on('auth:failure', handleAuthFailure);

    return () => {
      networkEventManager.off('auth:failure', handleAuthFailure);
    };
  }, []);

  return (
    <ChannelsContext.Provider value={{
      channels,
      loading,
      loadingMore,
      hasNextPage,
      totalCount,
      filterByClaimId,
      setFilterByClaimId: handleSetFilterByClaimId,
      refreshChannels,
      loadMoreChannels,
      updateChannelMessages,
      addMessageToChannel,
      updateChannelModel,
      deleteChannel,
      renameChannel,
      toggleChannelPin,
      closeChannel,
      toggleChannelShared,
      updateChannelTitle,
      reorderChannels,
      setChannels,
      sidebarSelectedTab,
      updateSidebarSelectedTab
    }}>
      {children}
    </ChannelsContext.Provider>
  );
};
